import { SpeakerInfo } from "~/TTS/voicevox/VoicevoxUtils";
import { AivisSpeechSpeakerInfo } from "~/TTS/aivisspeech/AivisSpeechUtils";
import * as db from "../components/db/CharacterDBUtils";
import { Buffer } from "buffer";
import { VRMList, Character } from "shared/Schemas";
import { VRM } from "@pixiv/three-vrm";
import { api } from "~/api";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";

const CHARACTER_TABLE_NAME: string = process.env.REACT_APP_AWS_DYNAMODB_CHARACTER_TABLE_NAME || "";

const ENV = process.env.REACT_APP_SYSTEM_REQUIREMENT || "onprem";
const vrmObjectUrlCache = new Map<string, string>();

// Helpers to manage blob URL cache with proper revoke semantics
export const setBlobObjectUrl = (key: string, url: string) => {
  const prev = vrmObjectUrlCache.get(key);
  if (prev && prev !== url) {
    try {
      URL.revokeObjectURL(prev);
    } catch {
      // ignore revoke errors
    }
  }
  vrmObjectUrlCache.set(key, url);
};

export const revokeBlobObjectUrl = (key: string) => {
  const prev = vrmObjectUrlCache.get(key);
  if (prev) {
    try {
      URL.revokeObjectURL(prev);
    } catch {
      // ignore
    }
    vrmObjectUrlCache.delete(key);
  }
};

export const revokeAllBlobObjectUrls = () => {
  for (const url of vrmObjectUrlCache.values()) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
  vrmObjectUrlCache.clear();
};

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  // Clean up blob URLs on full page unload/refresh to avoid memory leaks
  window.addEventListener("beforeunload", () => {
    try {
      revokeAllBlobObjectUrls();
    } catch {
      // swallow
    }
  });
}

const isBlobObjectUrl = (url?: string) => typeof url === "string" && url.startsWith("blob:");

export const getVRMName = (currentVRM: VRM | null) => {
  let name = "";
  if (!currentVRM) return "";
  if (currentVRM.meta?.metaVersion === "0") {
    // vrm.meta: VRM0Meta
    name = currentVRM && currentVRM.meta ? (currentVRM.meta.title as string) : "";
  } else if (currentVRM.meta?.metaVersion === "1") {
    // vrm.meta: VRM1Meta
    name = currentVRM && currentVRM.meta ? (currentVRM.meta.name as string) : "";
  }
  return name;
};
// キャラクターからキャラクターの英語名を取得
export const getVRMGlobalName = async (currentVRM: VRM | null): Promise<string> => {
  let globalName = "";
  const currentVRMName = getVRMName(currentVRM);

  if (ENV === "onprem") {
    // オンプレ環境の場合、SQLiteの処理
    // 非同期処理をawaitで待機
    const characters: Character[] = await db.selectAll();
    // currentCharacterと一致するキャラクターの番号を取得
    const currentIndex = characters.findIndex(
      (character: Character) => character.name === currentVRMName
    );
    // 該当するキャラクターが見つかればglobalNameを設定
    if (currentIndex !== -1) {
      globalName = characters[currentIndex].global_name;
    }
  } else {
    // AWS環境の場合、DynamoDBの処理
    try {
      const customerId = await getCurrentCustomerId();
      const response = await api.dynamodb.queryItems.query({
        tableName: CHARACTER_TABLE_NAME,
        filterKey: "customerId",
        filterValue: customerId,
      });

      if (response.success && response.items) {
        // DynamoDBのアイテムからcurrentVRMNameに一致するキャラクターを探す
        const dynamoItem = response.items.find((item) => {
          const record = item as Record<string, unknown>;
          return record.name === currentVRMName;
        });

        // DynamoDBアイテムが見つかればglobalNameを設定
        if (dynamoItem) {
          const record = dynamoItem as Record<string, unknown>;
          if (typeof record.globalName === "string") {
            globalName = record.globalName;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching characters from DynamoDB:", error);
    }
  }

  return globalName;
};
// eslint-disable-next-line complexity
export const getVRMURL = (vrmList: VRMList[], vrmName: string) => {
  const vrmData = vrmList.filter((vrm) => vrm.name === vrmName)[0] ?? vrmList[0];
  if (!vrmData) {
    throw new Error("No VRM file available");
  }

  // Prefer stable remote URLs. Persisted blob URLs become invalid after reload.
  if (vrmData.url && vrmData.url !== "" && !isBlobObjectUrl(vrmData.url)) {
    return vrmData.url;
  }

  if (vrmData.buffer) {
    const key = vrmData.path || vrmData.name || vrmName;
    const cachedUrl = vrmObjectUrlCache.get(key);
    if (cachedUrl) return cachedUrl;
    const modelBlob = new Blob([Buffer.from(vrmData.buffer.data)]);
    const url = URL.createObjectURL(modelBlob);
    setBlobObjectUrl(key, url);
    return url;
  }

  // Last resort for current-session blob URLs when buffer is unavailable.
  if (vrmData.url && vrmData.url !== "") {
    return vrmData.url;
  }

  throw new Error("No URL or buffer available for VRM file");
};

// eslint-disable-next-line complexity
export const getVRMImage = (currentVRM: VRM) => {
  let image: ImageBitmap | null | undefined = null;
  if (currentVRM.meta?.metaVersion === "0") {
    // vrm.meta: VRM0Meta
    image =
      currentVRM && currentVRM.meta && currentVRM.meta.texture && currentVRM.meta.texture.source
        ? (currentVRM.meta.texture.source.data as ImageBitmap | undefined)
        : null;
  } else if (currentVRM.meta?.metaVersion === "1") {
    // vrm.meta: VRM1Meta
    image =
      currentVRM && currentVRM.meta
        ? (currentVRM.meta.thumbnailImage as ImageBitmap | undefined)
        : null;
  }
  return image;
};

export const defaultSpeakerInfo: SpeakerInfo = {
  id: [3, 3],
  query: {
    accentPhrases: undefined,
    speedScale: undefined,
    pitchScale: undefined,
    intonationScale: undefined,
    volumeScale: undefined,
    prePhonemeLength: undefined,
    postPhonemeLength: undefined,
    outputSamplingRate: undefined,
    outputStereo: undefined,
    kana: undefined,
  },
};

export const LoppyInfo: SpeakerInfo = {
  id: [1, 75],
  query: {
    accentPhrases: undefined,
    speedScale: 1.05,
    pitchScale: -0.023,
    intonationScale: 0.9,
    volumeScale: 1.0,
    prePhonemeLength: 0.1,
    postPhonemeLength: 0.1,
    outputSamplingRate: undefined,
    outputStereo: undefined,
    kana: undefined,
  },
};

export const OpenAIInfo = {
  Model: "tts-1",
  Voice: "alloy",
};

export const defaultAivisSpeechInfo: AivisSpeechSpeakerInfo = {
  id: [1878365376], // コハク-normal
  query: {
    accentPhrases: undefined,
    speedScale: undefined,
    pitchScale: undefined,
    intonationScale: undefined,
    volumeScale: undefined,
    prePhonemeLength: undefined,
    postPhonemeLength: undefined,
    outputSamplingRate: undefined,
    outputStereo: undefined,
    kana: undefined,
  },
};

// キャラクター作成関数
export const createCharacter = async (name: string, globalName: string, engine: string) => {
  // 有効なエンジンかチェック
  const validEngines = ["VOICEVOX", "OpenAI", "AivisSpeech"];
  if (!validEngines.includes(engine)) {
    console.error("Unknown engine:", engine);
    return; // エラーの場合はinsertを呼ばない
  }

  // デフォルト値を使用（TTSエンジンの選択は tts_engine フィールドで制御される）
  const defaultVoicevoxSpeakerId = 2;
  const defaultVoicevoxSpeed = 1.0;
  const defaultVoicevoxPitch = 0.0;
  const defaultVoicevoxIntonation = 1.0;
  const defaultOpenaiSpeakerId = 1;
  const defaultAivisSpeechSpeakerId = defaultAivisSpeechInfo.id[0];
  const defaultAivisSpeechSpeed = defaultAivisSpeechInfo.query.speedScale || 1.0;
  const defaultAivisSpeechPitch = defaultAivisSpeechInfo.query.pitchScale || 0.0;
  const defaultAivisSpeechIntonation = defaultAivisSpeechInfo.query.intonationScale || 1.0;
  await db.insert(
    name,
    globalName,
    engine,
    "",
    defaultVoicevoxSpeakerId,
    defaultVoicevoxSpeed,
    defaultVoicevoxPitch,
    defaultVoicevoxIntonation,
    defaultOpenaiSpeakerId,
    defaultAivisSpeechSpeakerId,
    defaultAivisSpeechSpeed,
    defaultAivisSpeechPitch,
    defaultAivisSpeechIntonation
  );
};

/**
 * VRM配列をcharactersテーブルの登録順（name一致）でソートする関数
 *
 * @param vrms VRMリスト
 * @param characters charactersテーブルデータ
 * @returns charactersテーブルの登録順にソートされたVRMリスト
 */
export const sortVRMsByCharacterOrder = (
  vrms: (VRM | null)[],
  characters: Pick<Character, "name">[]
) => {
  const UNREGISTERED_ORDER = Number.MAX_SAFE_INTEGER; // 未登録は末尾

  const orderMap = new Map<string, number>(characters.map((c, i) => [c.name, i]));
  const toOrder = (vrm: VRM) => orderMap.get(getVRMName(vrm)) ?? UNREGISTERED_ORDER;
  return vrms
    .filter((v): v is NonNullable<typeof v> => v !== null)
    .sort((a, b) => toOrder(a) - toOrder(b));
};

export const getVRMFiles = async () => {
  if (ENV === "onprem") {
    return (await api.animation.getVRMfiles.query()) as VRMList[];
  }

  try {
    const customerId = await getCurrentCustomerId();
    const files = (await api.animation.getVRMfilesAWS.query({
      customerId,
    })) as VRMList[];

    // 署名付きURLから全ファイルをダウンロードしてバッファに変換
    const filesWithBuffers = await Promise.all(
      files.map(async (file) => {
        try {
          const response = await fetch(file.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          const buffer = Buffer.from(arrayBuffer);

          return {
            name: file.name,
            buffer: { type: "Buffer" as const, data: Array.from(buffer) },
            url: "", // バッファを使用するのでURLは空
            extension: file.extension,
            path: file.path,
          };
        } catch (error) {
          console.error(`Failed to download VRM ${file.name}:`, error);
          throw error;
        }
      })
    );

    return filesWithBuffers;
  } catch (error) {
    console.error("Failed to get VRM files:", error);
    throw error;
  }
};

export const getVRMFile = async (vrmName: string) => {
  // TODO: Implement VRM file retrieval functionality
  console.info("VRM file requested:", vrmName);
};

export const getIdleMotion = async () => {
  if (ENV === "onprem") {
    return (await api.animation.getIdleMotion.query()) as { type: "Buffer"; data: number[] };
  } else {
    try {
      const customerId = await getCurrentCustomerId();
      const result = (await api.animation.getIdleMotionAWS.query({
        customerId,
      })) as { url: string };

      // 署名付きURLからバッファをダウンロード
      const response = await fetch(result.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file from presigned URL: ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);

      return { type: "Buffer", data: Array.from(buffer) } as { type: "Buffer"; data: number[] };
    } catch (error) {
      console.error("Failed to get idle motion:", error);
      throw error;
    }
  }
};

export const getAnimationFiles = async () => {
  if (ENV === "onprem") {
    return await api.animation.getAnimationFiles.query();
  } else {
    try {
      const customerId = await getCurrentCustomerId();
      const files = await api.animation.getAnimationFilesAWS.query({
        customerId,
      });

      // 署名付きURLから全ファイルをダウンロードしてバッファに変換
      const filesWithBuffers = await Promise.all(
        files.map(async (file) => {
          try {
            const response = await fetch(file.url);
            if (!response.ok) {
              throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            const buffer = Buffer.from(arrayBuffer);

            return {
              name: file.name,
              buffer: { type: "Buffer" as const, data: Array.from(buffer) },
              url: "", // バッファを使用するのでURLは空
              extension: file.extension,
              path: file.path,
            };
          } catch (error) {
            console.error(`Failed to download ${file.name}:`, error);
            throw error;
          }
        })
      );

      return filesWithBuffers;
    } catch (error) {
      console.error("Failed to get animation files:", error);
      throw error;
    }
  }
};
